import { z } from 'zod';

/** Goal names: `lead_submit`/`booking_open`/`giveaway_entry` are fired by components, the rest by the click listener. */
export const analyticsGoals = ['lead_submit', 'booking_open', 'giveaway_entry', 'phone_click', 'telegram_click', 'max_click', 'document_download', 'outbound_click'] as const;
export type AnalyticsGoal = (typeof analyticsGoals)[number];
export const analyticsGoalLabels: Record<AnalyticsGoal, string> = {
  lead_submit: 'Отправка заявки',
  booking_open: 'Открытие формы записи',
  giveaway_entry: 'Участие в конкурсе',
  phone_click: 'Клик по телефону',
  telegram_click: 'Переход в Telegram',
  max_click: 'Переход в MAX',
  document_download: 'Скачивание документа',
  outbound_click: 'Внешняя ссылка',
};

export const sourceTypes = ['direct', 'search', 'social', 'referral', 'ad', 'internal', 'email'] as const;
export type SourceType = (typeof sourceTypes)[number];
export const sourceTypeLabels: Record<SourceType, string> = {
  direct: 'Прямые заходы',
  search: 'Поисковые системы',
  social: 'Социальные сети',
  referral: 'Переходы по ссылкам',
  ad: 'Реклама',
  internal: 'Внутренние переходы',
  email: 'Почтовые рассылки',
};
export const deviceTypes = ['desktop', 'mobile', 'tablet'] as const;
export type DeviceType = (typeof deviceTypes)[number];
export const deviceTypeLabels: Record<DeviceType, string> = { desktop: 'Компьютеры', mobile: 'Смартфоны', tablet: 'Планшеты' };

/** Batch limits; the client splits larger queues. */
export const analyticsLimits = { items: 30, path: 300, title: 300, selector: 300, href: 200, text: 80, referrer: 2000, utm: 200 } as const;

const uuid = z.string().uuid();
const pathSchema = z.string().min(1).max(analyticsLimits.path).regex(/^\/(?!\/)[^\s?#]*$/);
const utmValue = z.string().trim().min(1).max(analyticsLimits.utm);
const pixels = z.number().int().min(0).max(100_000);

const pageviewItem = z.object({
  type: z.literal('pageview'),
  /** Client-generated per-pageview id (never persisted client-side); lets a later consent attach this view to the new session. */
  id: uuid,
  path: pathSchema,
  title: z.string().max(analyticsLimits.title),
  /** Absolute external URL (origin + path, no query) on the landing hit, or the previous internal path afterwards. */
  referrer: z.string().max(analyticsLimits.referrer).optional(),
  utm: z.object({ source: utmValue.optional(), medium: utmValue.optional(), campaign: utmValue.optional(), term: utmValue.optional(), content: utmValue.optional() }).strict().optional(),
  adClick: z.enum(['yclid', 'gclid']).optional(),
  screen: z.string().regex(/^\d{1,5}x\d{1,5}$/).optional(),
  viewport: pixels.optional(),
  language: z.string().max(35).optional(),
  timezone: z.string().max(64).optional(),
  webdriver: z.boolean().optional(),
}).strict();

const engagementItem = z.object({
  type: z.literal('engagement'),
  pageviewId: uuid,
  timeMs: z.number().int().min(0).max(86_400_000),
  scrollPct: z.number().int().min(0).max(100),
}).strict();

const clickItem = z.object({
  type: z.literal('click'),
  pageviewId: uuid,
  path: pathSchema,
  selector: z.string().min(1).max(analyticsLimits.selector),
  /** Click position inside the element's box, 0..1. */
  ex: z.number().min(0).max(1),
  ey: z.number().min(0).max(1),
  /** Document coordinates, document size and viewport width at click time. */
  px: pixels, py: pixels, dw: pixels, dh: pixels, vw: pixels,
  href: z.string().max(analyticsLimits.href).optional(),
  text: z.string().max(analyticsLimits.text).optional(),
}).strict();

const goalItem = z.object({
  type: z.literal('goal'),
  name: z.enum(analyticsGoals),
  pageviewId: uuid.optional(),
  path: pathSchema,
  href: z.string().max(analyticsLimits.href).optional(),
}).strict();

export const collectItemSchema = z.discriminatedUnion('type', [pageviewItem, engagementItem, clickItem, goalItem]);

/**
 * POST /api/metrics/collect. With consent `visitorId` + `sessionId` are both present; without
 * consent both are absent and only pageviews and the `lead_submit` goal are accepted.
 */
export const collectSchema = z.object({
  visitorId: uuid.optional(),
  sessionId: uuid.optional(),
  items: z.array(collectItemSchema).min(1).max(analyticsLimits.items),
}).strict().superRefine((batch, context) => {
  if (Boolean(batch.visitorId) !== Boolean(batch.sessionId)) context.addIssue({ code: z.ZodIssueCode.custom, path: ['sessionId'], message: 'visitorId и sessionId передаются вместе' });
  if (batch.visitorId) return;
  batch.items.forEach((item, index) => {
    if (item.type === 'pageview' || (item.type === 'goal' && item.name === 'lead_submit' && !item.pageviewId && !item.href)) return;
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['items', index], message: 'Без согласия принимаются только анонимные просмотры' });
  });
});
export type CollectBatch = z.output<typeof collectSchema>;
export type CollectItem = z.output<typeof collectItemSchema>;

/** `count` is sessions for session breakdowns and pageviews for the anonymous ones; `share` is 0..1. */
export type Share = { key: string; count: number; share: number };
export type AnalyticsReport = {
  range: { from: string; to: string };
  summary: {
    visitors: number; newVisitors: number; returningVisitors: number;
    sessions: number; pageviews: number; anonymousPageviews: number;
    bounceRate: number; avgDurationMs: number; pageDepth: number;
  };
  goals: Array<{ name: AnalyticsGoal; events: number; anonymousEvents: number; conversions: number; conversionRate: number }>;
  daily: Array<{ date: string; sessions: number; visitors: number; pageviews: number }>;
  breakdowns: {
    sourceTypes: Share[]; searchEngines: Share[]; socialNetworks: Share[]; referrerHosts: Share[];
    utmSources: Share[]; utmCampaigns: Share[]; landingPages: Share[];
    devices: Share[]; browsers: Share[]; os: Share[]; screens: Share[];
  };
  anonymous: { sourceTypes: Share[]; devices: Share[] };
  pages: Array<{ path: string; title: string; views: number; avgTimeMs: number | null; avgScrollPct: number | null }>;
  realtime: { online: number; recent: Array<{ at: string; path: string; title: string; deviceType: DeviceType | null; sourceType: SourceType | null; anonymous: boolean }> };
};

export type ClickMapDevice = 'desktop' | 'mobile';
/** Viewports wider than the site's mobile breakpoint (public.css, 850px) count as desktop clicks. */
export const clickMapDesktopMinWidth = 851;
export type ClickMapReport = {
  path: string;
  device: ClickMapDevice;
  paths: Array<{ path: string; clicks: number }>;
  total: number;
  capped: boolean;
  points: Array<{ selector: string; ex: number; ey: number; px: number; py: number; dw: number; dh: number; vw: number; count: number }>;
  elements: Array<{ selector: string; text: string; href: string; clicks: number }>;
};
